import React, { useState, useRef, useMemo } from "react";
import { bool, func } from "prop-types";
import { useTranslation } from "@lib/i18n";
import getConfig from "next/config";

import CopyIcon from "@components/shared/Icons/Copy";
import CheckmarkIcon from "@components/shared/Icons/Checkmark";
import ShareIcon from "@components/shared/Icons/ShareIcon";
import { Button } from "../shared/Button";
import Modal from "../shared/Modal";

import { toast } from "@lib/reactToastify";
import { sendConfiguration } from "@lib/sendConfiguration";
import { generateConfigurationUrl } from "@shared/helper/generateConfigurationURL";
import { IS_CLIENT, QUERY_ACTION_TYPES } from "@shared/constants";
import gtm from "@gtm/core";
import { toggleShareEmailModalEvent } from "@gtm/events/basket";

import {
  useConfigurationSelector,
  useBrandCodeSelector,
} from "@redux/reducers/appConfig";

import {
  useCarConfigurationSelector,
  useFinanceSimulationSelector,
  useSalesmanSelector,
  useBusinessModelSelector,
  useIsScrappageSelectedSelector,
} from "@redux/reducers/deal";
import { useDeeplinkPathname } from "@hooks/useDeeplinkPathname";

const { publicRuntimeConfig } = getConfig();
const { ROOT_URL_PREFIX } = publicRuntimeConfig;

const ShareConfigModal = ({ isOpen, closeModal }) => {
  const { t, i18n } = useTranslation();
  const selectedBusinessModel = useBusinessModelSelector();
  const appConfiguration = useConfigurationSelector();
  const isScrappageSelected = useIsScrappageSelectedSelector();
  const selectedSalesman = useSalesmanSelector();
  const brandCode = useBrandCodeSelector();

  const {
    accessory: selectedAccessories,
    service: selectedServices,
    preconfiguration: selectedPreconfiguration,
  } = useCarConfigurationSelector();

  const {
    journey: selectedJourney,
    financeWidgetParameters,
  } = useFinanceSimulationSelector();

  const [isSending, setIsSending] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isCopied, setIsCopied] = useState(false);

  const modalFirstElement = useRef();
  const modalLastElement = useRef();
  const copyTimeoutRef = useRef(null);

  const sendText = t("general.send");

  const deeplinkPathname = useDeeplinkPathname();

  const directUrl = useMemo(
    () => (IS_CLIENT ? `${window.location.origin}${deeplinkPathname}` : ""),
    [deeplinkPathname]
  );

  const shareData = useMemo(
    () => ({
      text: selectedPreconfiguration.label,
      title: selectedPreconfiguration.label,
      url: directUrl,
    }),
    [directUrl]
  );

  const handleCopyClick = async e => {
    e.preventDefault();

    try {
      const type = "text/plain";
      const blob = new Blob([shareData.url || ""], { type });
      const data = [new ClipboardItem({ [type]: blob })];

      await navigator.clipboard.write(data);

      setIsCopied(true);
      clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setIsCopied(false), 3000);
    } catch (e) {
      console.error("Error writing to clipboard", e);
    }
  };

  const handleUrlInputClick = e => {
    e.target?.select?.();
    handleCopyClick(e);
  };

  const canShare = IS_CLIENT && navigator?.canShare;

  const handleShareClick = async e => {
    e.preventDefault();

    if (!canShare) {
      return;
    }

    try {
      await navigator.share(shareData);
    } catch (e) {
      console.error("Error sharing", e);
    }
  };

  const shareConfiguration = async event => {
    event.preventDefault();

    gtm.fire(
      toggleShareEmailModalEvent({
        envoyerAnnuler: "envoyer",
        label: sendText,
      })
    );

    setIsSending(true);

    const configurationUrl =
      ROOT_URL_PREFIX +
      generateConfigurationUrl({
        action: QUERY_ACTION_TYPES.SHARE_CONFIGURATION,
        selectedLanguage: i18n.language,
        appConfiguration,
        selectedAccessories,
        selectedServices,
        selectedPreconfiguration,
        selectedJourney,
        selectedSalesman,
        selectedBusinessModel,
        financeWidgetParameters,
        isScrappageSelected,
      });

    const result = await sendConfiguration(
      email,
      name,
      selectedPreconfiguration,
      configurationUrl,
      brandCode
    );

    setIsSending(false);

    if (result) {
      toast.success(t("basket.shareConfigurationModal.notification"));
      setName("");
      setEmail("");
      closeModal(true);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      firstElement={modalFirstElement}
      lastElement={modalLastElement}
      ariaLabel={t("basket.shareConfigurationModal.headerText")}
      onClose={() => closeModal(false)}
      withCloseIcon
      closeButtonDataId="share-configuration-modal-close"
    >
      <div className="flex flex-col gap-4 w-full">
        <div className="flex pr-8 w-full">
          <h1 className=" m-0 p-0 text-2xl text-primaryDark font-semibold">
            {t("basket.shareConfigurationModal.headerText")}
          </h1>
        </div>
        <div className="flex flex-col gap-4">
          <form className="flex flex-col gap-2" onSubmit={shareConfiguration}>
            <div className="flex flex-col self-start gap-1 w-full">
              <label className="text-sm text-primaryDark">
                {t("basket.shareConfigurationModal.nameDescription")} *
              </label>
              <input
                className="w-full p-2 bg-white border border-greyMedium rounded-sm text-sm/5"
                required
                placeholder={t("basket.shareConfigurationModal.name")}
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                data-id="share-configuration-modal-name"
              />
            </div>
            <div className="flex flex-col self-start gap-1 w-full">
              <label className="text-sm text-primaryDark">
                {t("basket.shareConfigurationModal.emailDescription")} *
              </label>
              <input
                className="w-full p-2 bg-white border border-greyMedium rounded-sm text-sm/5"
                required
                placeholder={t("basket.shareConfigurationModal.email")}
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                data-id="share-configuration-modal-email"
              />
            </div>
            <div className="flex justify-between items-center gap-4">
              <div className="flex flex-col gap-1 text-xs text-primaryDarkAccent">
                <span>
                  * {t("basket.shareConfigurationModal.requiredFields")}
                </span>
                <span>{t("basket.shareConfigurationModal.footerText")}</span>
              </div>
              <Button
                isLoading={isSending}
                withBorder
                type="submit"
                data-id="share-configuration-modal-submit"
              >
                {sendText}
              </Button>
            </div>
          </form>
          <div className="flex flex-col gap-4 pt-4 border-t border-t-primaryOrange">
            <div className="flex flex-wrap justify-between items-center gap-4">
              <h2 className="text-xl text-primaryDark font-semibold m-0">
                {t("basket.shareConfigurationModal.shareAddress")}
              </h2>
              <div className="flex gap-1">
                <button
                  className="
                    grid place-items-center size-10 rounded-sm text-center transition duration-150 bg-greyMedium text-white cursor-pointer
                    max-sm:bg-primaryOrange
                    hover:bg-primaryOrange hover:scale-105 active:scale-95
                    [&>svg]:size-6
                  "
                  onClick={handleCopyClick}
                >
                  {isCopied ? <CheckmarkIcon checked={true} /> : <CopyIcon />}
                </button>
                {canShare && (
                  <button
                    className="
                      grid place-items-center size-10 rounded-sm text-center transition duration-150 bg-greyMedium text-white cursor-pointer
                      max-sm:bg-primaryOrange
                      hover:bg-primaryOrange hover:scale-105 active:scale-95
                      [&>svg]:size-6
                    "
                    onClick={handleShareClick}
                  >
                    <ShareIcon />
                  </button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap justify-between items-center gap-4 max-sm:pb-4">
              <input
                className="w-full p-2 bg-greyLight border border-greyDark rounded-sm text-sm/5"
                onClick={handleUrlInputClick}
                readOnly
                value={directUrl}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

ShareConfigModal.propTypes = {
  isOpen: bool.isRequired,
  closeModal: func.isRequired,
};

export default ShareConfigModal;
